import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { path, prop } from 'ramda';
import { throttle } from 'underscore';

import { usePortletConfig, usePortletSelector } from '@core/js/components/system/Portlet';

import { Category } from '@theme/js/components/main/Games/Category';

import { trackGameCategorySwitch } from '@core/js/components/main/Games/trackingApi';
import { useTranslations } from '@core/js/hooks/i18n';
import { usePrevious } from '@core/js/hooks/usePrevious';
import { useUpdateEffect } from '@core/js/hooks/useUpdateEffect';
import { useAppSelector, useDeviceSelector, useGameConfigurationSelector, useUserSelector } from '@core/js/store/hooks';

import { getWebContentNodes, handleScroll } from '@interlayer/js/components/main/Games/CategoriesTabs/sun/helpers';
import { useScrollToComponentTop } from '@interlayer/js/hooks/useScrollToComponentTop';

import style from './index.scss';

const transformStringToKebabCase = (str) => str.toLowerCase().replace(/[^a-z]+/g, '-');

export const useTrackCategoryChange = (activeCategory) => {
    const feedName = usePortletSelector(prop('feedName'));
    const { reactPortletId } = usePortletConfig();
    const prevCid = usePrevious(activeCategory.cid);

    useUpdateEffect(() => {
        if (prevCid !== activeCategory.cid) {
            trackGameCategorySwitch(reactPortletId, feedName, activeCategory.name);
        }
    }, [reactPortletId, feedName, activeCategory, prevCid]);
};

const NAV_MENU_DEFAULT_HEIGHT = 40;

export const CategoryTab = ({ category, onTabClick, isActive, dataAttribute }) => {
    const categoryRef = useRef(null);
    const handleScrollTop = useScrollToComponentTop(categoryRef, NAV_MENU_DEFAULT_HEIGHT);
    const t = useTranslations();
    const { cid, roundIcon, displayName, displayNameShort, mostPlayedGameCode } = category;
    const iconClass = style[roundIcon] || style.generic;
    const activeClass = isActive ? style.active : '';
    const fallbackDisplayName = t(displayName);
    const onClick = useCallback(() => {
        onTabClick(cid);
        handleScrollTop({ scrollToPortletTop: true });
    }, [cid, handleScrollTop, onTabClick]);
    const mostPlayedGame = useGameConfigurationSelector(path(['games', mostPlayedGameCode, 'name'])) || '';

    return (
        <div className={style.category} onClick={onClick} ref={categoryRef} { ...dataAttribute }>
            <div className={`${style.icon} ${iconClass} ${activeClass}`} />
            <div className={`${style.title} ${activeClass}`}>
                {t(displayNameShort, [mostPlayedGame], fallbackDisplayName)}
            </div>
        </div>
    );
};

const DEFAULT_MENU_HEIGHT = 46;

export const CategoriesTabs = ({ categories = [], sliders = [] }) => {
    const categoriesRef = useRef(null);
    const categoriesWrapperRef = useRef(null);
    const [activeCategoryId, setActiveCategoryId] = useState(path([0, 'cid'], categories));
    const headerHeight = useAppSelector(prop('headerHeight'));
    const prevPositionRef = useRef(window.scrollY);
    const isMobile = useDeviceSelector(prop('isMobile'));
    const { pathname: pathPage } = useLocation();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));

    const activeCategory = useMemo(
        () => categories.find(({ cid }) => cid === activeCategoryId) || categories[0],
        [categories, activeCategoryId]
    );

    useEffect(() => {
        const [body] = getWebContentNodes(['body', '.mobile-play-bingo-green']);

        const changePositionCategory = throttle(handleScroll({
            body,
            categoriesRef,
            categoriesWrapperRef,
            headerHeight: Math.floor(headerHeight + DEFAULT_MENU_HEIGHT),
            prevPositionRef
        }), 200);

        window.addEventListener('scroll', changePositionCategory);

        return () => {
            window.removeEventListener('scroll', changePositionCategory);
            body.classList.remove('menu-hidden');
        };
    }, [headerHeight]);

    useTrackCategoryChange(activeCategory);
    const transformedPathName = pathPage.slice(1) || 'home';

    if (!activeCategory) {
        return null;
    }

    return <>
        <div className={`${style.container} cms-games-category-tabs`} ref={categoriesWrapperRef}>
            <div className={style.wrapper} ref={categoriesRef}>
            {categories.map((category, index) => {
                    const transformedCategoryName = transformStringToKebabCase(category.name);
                    const dataAttribute =
                        isMobile && isLoggedIn
                            ? { [`data-${transformedPathName}-${transformedCategoryName}`]: index + 1 }
                            : null;

                    return (
                        <CategoryTab
                            key={category.cid}
                            category={category}
                            dataAttribute={dataAttribute}
                            onTabClick={setActiveCategoryId}
                            isActive={category.cid === activeCategory.cid}
                                                    />
                    );
                })}
            </div>
        </div>
        {sliders.map(category => (
            <Category
                key={category.cid}
                {...category}
            />
        ))}
        <Category showTitle={false} {...activeCategory} />
    </>;
};

export default CategoriesTabs;
